import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * BroadcastFrame — a fixed 1920×1080 "broadcast" design space that scales to fit
 * (or fill) whatever pane it lands in. Everything on the Stage is authored at this
 * one resolution; the frame measures its host and applies a single uniform
 * transform, so a 4K TV, a laptop, and a small Discord activity rail all render
 * pixel-for-pixel the same composition — just at a different scale.
 *
 * fit="contain" (default): letterbox — scale to the largest size that fits inside
 * the pane, filling the leftover margin with the ink letterbox color. Use for the
 * main TV/Stage view.
 * fit="fill": scale to COVER the pane (may crop top/bottom or sides). Use for a
 * small always-on pane like the Discord rail where empty bars are undesirable.
 *
 * SAFE (64px) is the title-safe inset: keep load-bearing text/logos this far from
 * the 1920×1080 edge so nothing is clipped on overscanning TVs or by fill-crop.
 */

/** Design-space dimensions. All Stage content is authored against these. */
export const DESIGN_WIDTH = 1920;
export const DESIGN_HEIGHT = 1080;

/** Title-safe inset (px, in design space) — keep key content this far from edges. */
export const SAFE = 64;

export interface BroadcastFrameProps {
  children?: ReactNode;
  /** contain = letterbox to fit (default); fill = scale to cover (may crop). */
  fit?: 'contain' | 'fill';
  /** Letterbox background (the bars around a contained frame). Default --ink-0. */
  background?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function BroadcastFrame({
  children,
  fit = 'contain',
  background = 'var(--ink-0, #060406)',
  className,
  style,
}: BroadcastFrameProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const measure = () => {
      const { width, height } = host.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      const sx = width / DESIGN_WIDTH;
      const sy = height / DESIGN_HEIGHT;
      // contain → fit inside (min); fill → cover (max).
      setScale(fit === 'fill' ? Math.max(sx, sy) : Math.min(sx, sy));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, [fit]);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background,
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: DESIGN_WIDTH,
          height: DESIGN_HEIGHT,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>
    </div>
  );
}
